import { Component, OnInit, EventEmitter, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  WmsDialogHeaderComponent,
  WmsDialogBodyComponent,
  WmsDialogFooterComponent,
  BaseComponent,
  WmsMessageComponent,
  BindLocalizedMessagesComponent,
  BindVariableComponent,
  BindWidgetsComponent,
  BindResourceComponent,
  BindUseExpressionComponent,
  BindPrefabPropertiesComponent,
  BindPageParamsComponent,
  WmsTreeComponent
} from '@wms/components';
import { FormsModule } from '@angular/forms';
import { isEmpty, pull } from 'lodash-es';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { BaseVariableService } from '@wms/variables';
import {
  BindTreeUtilsService,
  NotificationService,
  BindVariableService
} from '@wms/services';
import { OrderByPipe, FilterPipe } from '@wm/components-base';
import { WmsFeatureDirective } from '@wms/directives';
import { PageWsService } from '@wms/workspace';
import { isValidExpr } from '@wm/core';
import { BindWidgetsVariablesUtilsService } from "@wms/app/services/bind-widgets-variables-utils/bind-widgets-variables-utils.service";
import {AppLocaleManagerService, LocaleMessagesTabComponent} from "@wms/i18n";

@Component({
  selector: 'wms-bind-dialog-ui',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TabsModule,
    WmsFeatureDirective,
    WmsDialogHeaderComponent,
    WmsDialogBodyComponent,
    WmsDialogFooterComponent,
    WmsMessageComponent,
    OrderByPipe,
    FilterPipe,
    BindVariableComponent,
    BindWidgetsComponent,
    BindResourceComponent,
    BindUseExpressionComponent,
    BindLocalizedMessagesComponent,
    BindPrefabPropertiesComponent,
    BindPageParamsComponent,
    WmsTreeComponent,
    LocaleMessagesTabComponent
  ],
  templateUrl: './bind-dialog-ui.component.html',
  styleUrls: ['./bind-dialog-ui.component.less']
})
export class BindDialogUiComponent extends BaseComponent implements OnInit {

  @Output() onModalClose: EventEmitter<any> = new EventEmitter<any>();
  prevBindNode: any;
  bindTreeService: BindTreeUtilsService;
  bindVariableService: BindVariableService;
  baseVariableService: BaseVariableService;
  pageWsService: PageWsService;
  notificationService: NotificationService;
  bindWidgetsVariablesUtilsService: BindWidgetsVariablesUtilsService;
  initResourcesSubscription: any;
  isExprLoaded: boolean;
  isWidgetsLoaded: boolean;
  appLocalManagerService:AppLocaleManagerService;

  constructor() {
    super();
    this.bindTreeService = inject(BindTreeUtilsService);
    this.bindVariableService = inject(BindVariableService);
    this.notificationService = inject(NotificationService);
    this.baseVariableService = inject(BaseVariableService);
    this.appLocalManagerService = inject(AppLocaleManagerService);
    this.bindWidgetsVariablesUtilsService = inject(BindWidgetsVariablesUtilsService);
    this.pageWsService = inject(PageWsService);
  }

  unBindVariable() {
    this.addUpdateBinding("");
    this.bindVariableService.activeBindNode.self.active = true;
    let activeNode = this.bindVariableService.selectedNode;
    let newVal = this.bindVariableService.activeBindNode.id;
    this.bindVariableService.selectedNode = { ...activeNode, dataItem: { ...activeNode.dataItem, label: newVal, badgeLabel: null } };
    if (this.prevBindNode && this.prevBindNode.active) {
      this.prevBindNode.active = false;
    }
  }

  showWarningMsg() {
    return isEmpty(this.bindVariableService.warningMessage);
  }

  handleBindTreeNodeClick(node: any) {
    const { dataItem, id } = node;
    const { props } = dataItem;
    this.prevBindNode = this.bindVariableService.activeBindNode?.self;
    this.bindVariableService.activeProperty.name = this.bindVariableService.dialogParams.activeProperty.propertyOwnerName + '.' + props.bindTarget;
    this.bindVariableService.activeProperty.type = props.type;
    this.bindVariableService.activeProperty.variableBindTree = true;
    this.bindVariableService.activeProperty.format = props.format;
    this.bindVariableService.activeProperty.bindingType = props.bindingType;
    this.bindVariableService.activeProperty.typeLabel = this.bindTreeService.getNodeInfoLabel(this.bindVariableService.activeProperty.format || this.bindVariableService.activeProperty.type, this.bindVariableService.activeProperty.bindingType, true);
    this.bindVariableService.activeBindNode = {
      props: props,
      id: dataItem.id,
      self: dataItem
    };
    this.bindVariableService.resetEditorContent();
    this.bindVariableService.validateBinding();
    this.bindVariableService.selectedNode = node;
  }

  closeModal() {
  this.appLocalManagerService.showNewLocaleMsgScreen = false;
    this.onModalClose.emit();
  }

  onCancel(event: any) {
    this.closeModal();
  }

  onDone($event: any) {
    if (!this.bindVariableService.dialogParams.nestedBindMode) {
      /* if normal mode, assign the bound value to the target property model */
      //TO-DO - get boundVariable value
      //this.bindVariableService.dialogParams.propertyDetails.modelObj[this.bindVariableService.dialogParams.propertyDetails.modelKey] = boundVariable;
    } else {
      /* new bindings are made, re-render the UI for bindings */
      this.baseVariableService.addBindingsToProperties();
    }
    this.baseVariableService.markUpdated();
    this.closeModal();
  }

  /**
   * handles click on the BIND button
   * [After 8.2.3]: allow binding even if validation fails. Show only warning message
   */
  bindVariable = (isFromAddCustomFormatter?: any) => {
    let expression,
      boundEntityName,
      boundEntityType,
      message,
      selectedNodeProperties = this.bindVariableService.getSelectedNodePropObj();

    /* if active tab is expressions */
    if (this.bindVariableService.activeTab[this.bindVariableService.tabs[3]]) {
      this.bindVariableService.updateEditorContentSubject.next({ 'get': true });
      expression = this.bindVariableService.useExprEditorContent;
      if (!expression) {
        /*warn user for empty expression*/
        this.bindVariableService.showWarning = true;
        this.bindVariableService.warningMessage = {
          type: "warning",
          caption: this.locale["MESSAGE_EMPTY_BINDEXPR"]
        }
        if (isFromAddCustomFormatter) {
          this.closeModal();
        }
        return;
      }

      /*to check if the bindExpression has any syntax-error*/
      if (!isValidExpr(expression)) {
        /* throw the syntax-error notification to the user & return*/
        this.notificationService.show('error', this.locale["MESSAGE_ERROR_TITLE"], this.locale["MESSAGE_SYNTAX_ERROR_DISPLAYEXPR"]);
        return;
      }

      expression = "bind:" + expression;
      boundEntityName = expression;
      boundEntityType = 'expression';
    } else if (this.bindVariableService.activeTab[this.bindVariableService.tabs[4]]) {
      if (selectedNodeProperties) {
        expression = selectedNodeProperties && selectedNodeProperties.value;
        if (!expression) {
          return;
        }

        /* construct the expression used to update the markup */
        expression = this.bindVariableService.appLocaleBindExpr + '.' + expression;
        boundEntityName = expression;
        boundEntityType = 'expression';
      } else {
        this.bindVariableService.showWarning = true;
        message = this.bindVariableService.tabs[this.bindVariableService.activeTab.index];
        this.bindVariableService.warningMessage = {
          type: "warning",
          caption: this.locale.BIND_VALUE_NOT_SELECTED_MESSAGE.replace("${0}", [this.bindVariableService.tabs[this.bindVariableService.activeTab.index]])
        }
        return;
      }
    } else {
      //Throw warning if property is not binded to any node
      if (!selectedNodeProperties || !this.bindVariableService.activeTab[selectedNodeProperties.type]) {
        if (this.bindVariableService.tabs[this.bindVariableService.activeTab.index] === 'variable' || this.bindVariableService.tabs[this.bindVariableService.activeTab.index] === 'widget') {
          message = this.bindVariableService.tabs[this.bindVariableService.activeTab.index] + ' property';
        } else {
          message = this.bindVariableService.tabs[this.bindVariableService.activeTab.index];
        }
        this.bindVariableService.showWarning = true;
        this.bindVariableService.warningMessage = {
          type: "warning",
          caption: this.locale.BIND_VALUE_NOT_SELECTED_MESSAGE.replace("${0}", message)
        }
        return;
      }
      /*conditional to check if the bind click property is disabled or enabled to continue further.*/
      boundEntityName = '';
      boundEntityType = '';
      if (selectedNodeProperties) {
        boundEntityType = selectedNodeProperties.type;
        switch (selectedNodeProperties.type) {
          case 'resource':
            boundEntityName = selectedNodeProperties.path;
            break;
          case 'widget':
            boundEntityName = selectedNodeProperties.value;
            boundEntityName = "bind:Widgets." + boundEntityName;
            break;
          case 'variable':
            boundEntityName = selectedNodeProperties.value;
            boundEntityName = "bind:Variables" + (boundEntityName.charAt(0) === '[' ? '' : '.') + boundEntityName;
            break;
          case 'prefab-props':
          case 'page-param':
            boundEntityName = selectedNodeProperties.value;
            boundEntityName = "bind:" + boundEntityName;
            break;
        }
      }
    }

    if (this.bindVariableService.variableMode) {

      /* create/update the target binding object */
      this.addUpdateBinding(boundEntityName);

      this.bindVariableService.activeBindNode.self.active = true;
      let activeNode = this.bindVariableService.selectedNode,
        newVal, badgeLabel;
      if (boundEntityName !== undefined && boundEntityName !== null) {
        newVal = this.bindVariableService.activeBindNode.id;
        badgeLabel = boundEntityName.replace("bind:", "").replace("Variables.", "").replace("Widgets.", "");
        this.bindVariableService.selectedNode = { ...activeNode, dataItem: { ...activeNode.dataItem, label: newVal,  badgeLabel} };
      } else {
        newVal = this.bindVariableService.activeBindNode.id;
        this.bindVariableService.selectedNode = { ...activeNode, dataItem: { ...activeNode.dataItem, label: newVal, badgeLabel: null } };
      }
      if (this.prevBindNode && this.prevBindNode.active) {
        this.prevBindNode.active = false;
      }
      return;
    }
    this.close(boundEntityName, boundEntityType);

  }

  close(boundEntityName: any, boundEntityType: any) {
    if(this.bindVariableService.dialogParams?.propertyBindCallback){
      this.bindVariableService.dialogParams.propertyBindCallback(boundEntityName, this.bindVariableService.dialogParams.activeProperty.name, boundEntityType);
    }
    this.closeModal();
  }

  /**
   * finds the binding object against the selected node (from left tree) from existingBindings of active variable
   * if not found creates a new object and pushes into the bindings
   * assigns the object with the passed value
   * @param value
   */
  addUpdateBinding(value: any) {
    let targetBindNode = this.bindVariableService.getBindingObjectFromList(this.bindVariableService.activeBindNode.props.bindTarget, true),
      targetNodeLable = targetBindNode.target || "";
    targetBindNode.value = value;
    /* if the bind node is dataBinding or on a level after first level, remove it from bindings */
    if (value === "" && (targetNodeLable === "dataBinding" || targetNodeLable.indexOf(".") !== -1)) {
      pull(this.bindVariableService.dialogParams.activeProperty.existingBindings, targetBindNode);
      return;
    }
  }

  ngOnInit(): void {
    if (isEmpty(this.i18nService.localeBundle)) {
      this.i18nService.getLocaleBundle();
    }
    if(this.bindVariableService.dialogParams.showPageSelection) {
      this.bindWidgetsVariablesUtilsService.init();
    } else {
      this.bindVariableService.initVariables();
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.initResourcesSubscription) {
      this.initResourcesSubscription.unsubscribe();
    }
    //this.bindVariableService.initVariables(true);
    this.bindVariableService.variableMode = false;
    this.bindVariableService.useExprEditorContent = '';
    if(this.bindVariableService.dialogParams.showPageSelection) {
      this.bindWidgetsVariablesUtilsService.clearAll();
    }
    this.bindVariableService.isWidgetsLoaded = false;
    this.bindVariableService.isVariablesLoaded = false;
    this.bindVariableService.appMessagesInitialized = false;
    this.bindVariableService.displayExprTeeLoaded = false;
    this.bindVariableService.mixedTree = [];
  }

}
